"use client";

import { useEffect, useState } from "react";
import * as Sentry from "@sentry/nextjs";
import {
  detectCurrentInAppBrowser,
  mobilePlatform,
  type InAppBrowserApp,
  type MobilePlatform,
} from "src/utils/in-app-browser";
import { isEmbeddedInApp } from "src/utils/embedded-app";

const KNOWN: InAppBrowserApp[] = [
  "facebook",
  "instagram",
  "tiktok",
  "snapchat",
  "pinterest",
  "linkedin",
  "line",
];

export interface InAppBrowserState {
  /** The social app whose WebView this is, or null in a real browser. */
  app: InAppBrowserApp | null;
  platform: MobilePlatform;
  /**
   * `app` comes from `?inappPreview=`, not the user agent: a reviewer in a
   * normal browser. Shown the same, but never measured as an in-app visit.
   */
  preview: boolean;
}

/**
 * The social in-app browser (Facebook, Instagram, ...) the shop is open in, if
 * any. Client-only — it needs navigator.userAgent — so it is null on the
 * server and on the first client paint, then set after mount; that keeps
 * hydration clean.
 *
 * Never set inside OUR mobile app's WebView (`isEmbeddedInApp`): telling an app
 * customer to "open this in your browser" would walk them out of the app.
 *
 * `?inappPreview=facebook` (or instagram, tiktok, ... ; any other value means
 * facebook) previews the in-app experience in a normal browser; the platform
 * still comes from the user agent, so a phone UA shows its Android or iOS
 * variant.
 */
export function useInAppBrowser(): InAppBrowserState {
  const [state, setState] = useState<InAppBrowserState>({
    app: null,
    platform: "other",
    preview: false,
  });

  useEffect(() => {
    if (isEmbeddedInApp()) return;
    const platform = mobilePlatform(navigator.userAgent);
    let preview: string | null = null;
    try {
      preview = new URLSearchParams(window.location.search).get("inappPreview");
    } catch {
      preview = null;
    }
    if (preview) {
      const app = KNOWN.includes(preview as InAppBrowserApp)
        ? (preview as InAppBrowserApp)
        : "facebook";
      setState({ app, platform, preview: true });
      return;
    }
    const { isInApp, app } = detectCurrentInAppBrowser();
    if (isInApp) {
      setState({ app, platform, preview: false });
      // On every later event from this page (uploads, payments, sign-in), so
      // in-app failure rates can be compared with the real browsers directly.
      Sentry.setTag("inAppBrowser", app);
    }
  }, []);

  return state;
}
